import { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Heart, Loader2, MapPin, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const numberedIcon = (n) =>
  new L.DivIcon({
    html: `<div style="background:#059669;color:#fff;padding:4px 0;width:32px;height:32px;border-radius:9999px;font-size:13px;font-weight:800;box-shadow:0 2px 8px rgba(0,0,0,0.3);text-align:center;line-height:24px;border:2px solid white;">${n}</div>`,
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

const originIcon = new L.DivIcon({
  html: '<div style="background:#0d9488;color:#fff;padding:4px 8px;border-radius:9999px;font-size:11px;font-weight:700;box-shadow:0 2px 8px rgba(0,0,0,0.3);border:2px solid white;">START</div>',
  className: "",
  iconSize: [56, 24],
  iconAnchor: [28, 12],
});

const STATUS_TONE = {
  requested: "bg-slate-100 text-slate-800",
  scheduled: "bg-blue-100 text-blue-800",
  en_route: "bg-amber-100 text-amber-800",
  arrived: "bg-blue-200 text-blue-900",
  in_progress: "bg-emerald-100 text-emerald-800",
};

async function hcGet(auth, path) {
  const res = await fetch(`${API}/homecare${path}`, { headers: { "X-Dispatcher-Auth": auth } });
  if (res.status === 401) throw new Error("UNAUTHORIZED");
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Server ${res.status}`);
  return data;
}

export function HomecareSweepPanel({ auth }) {
  const [caregivers, setCaregivers] = useState([]);
  const [caregiverId, setCaregiverId] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load caregivers for the filter dropdown
  useEffect(() => {
    if (!auth) return;
    hcGet(auth, "/admin/caregivers").then((d) => setCaregivers(d.caregivers || [])).catch(() => {});
  }, [auth]);

  const load = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ date });
      if (caregiverId) q.set("caregiver_id", caregiverId);
      const data = await hcGet(auth, `/admin/visits/route?${q.toString()}`);
      setRoute(data);
    } catch (e) { toast.error(e.message || "Failed to load route"); }
    finally { setLoading(false); }
  }, [auth, caregiverId, date]);

  useEffect(() => { load(); }, [load]);

  const withCoords = route?.route || [];
  const origin = route?.origin;
  const center = useMemo(() => {
    if (origin) return [origin.lat, origin.lng];
    if (withCoords.length === 0) return null;
    return [
      withCoords.reduce((s, v) => s + v.address_lat, 0) / withCoords.length,
      withCoords.reduce((s, v) => s + v.address_lng, 0) / withCoords.length,
    ];
  }, [origin, withCoords]);

  const polyPositions = [];
  if (origin) polyPositions.push([origin.lat, origin.lng]);
  withCoords.forEach((v) => polyPositions.push([v.address_lat, v.address_lng]));

  return (
    <div className="space-y-4" data-testid="homecare-sweep-panel">
      <div className="bg-white border border-brand-navy/10 rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <Heart className="w-5 h-5 text-emerald-700" />
        <div className="text-sm font-semibold text-brand-deep">Home Care route</div>
        <select value={caregiverId} onChange={(e) => setCaregiverId(e.target.value)}
          data-testid="hc-sweep-caregiver"
          className="text-sm border border-brand-navy/15 rounded-full px-3 py-1.5">
          <option value="">All caregivers (fleet centroid)</option>
          {caregivers.map((c) => (
            <option key={c.id} value={c.id}>{c.name} · {c.caregiver_type}</option>
          ))}
        </select>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          data-testid="hc-sweep-date"
          className="text-sm border border-brand-navy/15 rounded-full px-3 py-1.5" />
        <button onClick={load} disabled={loading} data-testid="hc-sweep-refresh"
          className="ml-auto inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-800">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {loading && !route && (
        <div className="bg-white border border-brand-navy/10 rounded-2xl p-6 text-center text-sm text-brand-muted flex items-center justify-center gap-2" data-testid="hc-sweep-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Optimizing home care route…
        </div>
      )}

      {!loading && withCoords.length === 0 && (
        <div className="bg-white border border-brand-navy/10 rounded-2xl p-8 text-center" data-testid="hc-sweep-empty">
          <MapPin className="w-8 h-8 text-brand-muted mx-auto mb-2" />
          <div className="text-sm text-brand-muted">
            No home care visits{caregiverId ? " for this caregiver" : ""} on {date}.
          </div>
        </div>
      )}

      {withCoords.length > 0 && (
        <>
          <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden">
            <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2 flex-wrap">
              <Heart className="w-4 h-4 text-emerald-700" />
              <div className="text-sm font-semibold text-emerald-900">
                {withCoords.length} visit{withCoords.length === 1 ? "" : "s"}
                {route?.total_miles ? (
                  <span className="ml-2 text-emerald-800 font-normal" data-testid="hc-sweep-total-miles">
                    · ~{route.total_miles} mi total
                  </span>
                ) : null}
              </div>
              <div className="ml-auto text-[11px] text-emerald-800/80 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Numbered in nearest-neighbor order
              </div>
            </div>
            <div style={{ height: 400 }}>
              <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://osm.org">OpenStreetMap</a>'
                />
                {origin && (
                  <Marker position={[origin.lat, origin.lng]} icon={originIcon}>
                    <Popup>Route starts here</Popup>
                  </Marker>
                )}
                {polyPositions.length >= 2 && (
                  <Polyline positions={polyPositions}
                    pathOptions={{ color: "#059669", weight: 4, opacity: 0.75, dashArray: "8,8" }} />
                )}
                {withCoords.map((v) => (
                  <Marker key={v.id} position={[v.address_lat, v.address_lng]} icon={numberedIcon(v.seq)}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-semibold">#{v.seq} · {v.member_name || "Visit"}</div>
                        <div className="text-xs text-slate-600">{v.caregiver_type} · {v.visit_type?.replace("_", " ")}</div>
                        <div className="text-xs text-slate-600 mt-1">📍 {v.address}</div>
                        <div className="text-xs text-slate-600">🕒 {new Date(v.start_at).toLocaleString()}</div>
                        {v.caregiver_name && <div className="text-xs text-emerald-700 mt-1">Assigned: {v.caregiver_name}</div>}
                        {typeof v.leg_miles === "number" && (
                          <div className="text-[11px] text-slate-500 mt-1">{v.leg_miles} mi from prev</div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden">
            <div className="px-4 py-2 bg-brand-bg text-xs uppercase tracking-[0.14em] font-semibold text-brand-muted">
              Ordered stops
            </div>
            <ul className="divide-y divide-brand-navy/5">
              {withCoords.map((v) => (
                <li key={v.id} className="px-4 py-3 flex items-start gap-3 text-sm" data-testid={`hc-sweep-row-${v.seq}`}>
                  <div className="w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {v.seq}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-brand-deep truncate">
                      {v.member_name || "Home care visit"} · <span className="text-brand-muted font-normal">{v.caregiver_type}</span>
                    </div>
                    <div className="text-xs text-brand-muted truncate">
                      {v.address} · {new Date(v.start_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                      {typeof v.leg_miles === "number" ? ` · ${v.leg_miles} mi` : ""}
                    </div>
                    {v.caregiver_name && (
                      <div className="text-xs text-emerald-700 truncate">Assigned: {v.caregiver_name}</div>
                    )}
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-[0.14em] ${STATUS_TONE[v.status] || "bg-slate-100 text-slate-700"}`}>
                    {(v.status || "").replace("_", " ")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
